/**
 * RotaryKnob Component
 * 
 * Professional-grade rotary knob control for audio effect parameters.
 * Emulates hardware knobs with realistic interaction and visual feedback.
 */

import { useCallback, useState, useRef, useEffect } from 'react';
import { Tooltip } from './Tooltip';
import './RotaryKnob.css';

/**
 * Size variants for the knob
 */
export type KnobSize = 'small' | 'medium' | 'large';

/**
 * Theme variants for the knob
 */
export type KnobTheme = 'modern' | 'retro';

/**
 * Props for RotaryKnob component
 */
export interface RotaryKnobProps {
  /** Current value */
  value: number;
  /** Minimum value */
  min: number;
  /** Maximum value */
  max: number;
  /** Default value (for reset) */
  default: number;
  /** Step size for quantization (optional) */
  step?: number;
  /** Label text */
  label: string;
  /** Unit text (e.g., 'Hz', 'dB', '%') */
  unit?: string;
  /** Callback when value changes during interaction */
  onChange: (value: number) => void;
  /** Callback when interaction completes (optional) */
  onChangeComplete?: (value: number) => void;
  /** Whether the knob is disabled */
  disabled?: boolean;
  /** Size variant */
  size?: KnobSize;
  /** Theme variant (defaults to current app theme) */
  theme?: KnobTheme;
  /** Tooltip text to display on hover (optional) */
  tooltip?: string;
}

/**
 * RotaryKnob component
 * 
 * Features:
 * - Vertical drag interaction
 * - Mouse wheel support
 * - Keyboard navigation
 * - Double-click to reset
 * - Smooth 60fps animation
 * - Accessibility compliant
 * 
 * @param props - Component props
 * @returns RotaryKnob component
 */
export function RotaryKnob({
  value,
  min,
  max,
  default: defaultValue,
  step = 0.01,
  label,
  unit = '',
  onChange,
  onChangeComplete,
  disabled = false,
  size = 'medium',
  theme,
  tooltip,
}: RotaryKnobProps) {
  // Local state for drag interaction
  const [isDragging, setIsDragging] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  
  // Refs for drag tracking
  const dragStartY = useRef<number>(0);
  const dragStartValue = useRef<number>(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const animationFrameRef = useRef<number | null>(null);
  const mouseMoveHandlerRef = useRef<((e: MouseEvent) => void) | null>(null);
  const mouseUpHandlerRef = useRef<((e: MouseEvent) => void) | null>(null);
  
  // Generate unique ID for this knob instance
  const uniqueId = useRef(`knob-${Math.random().toString(36).substr(2, 9)}`).current;

  /**
   * Clamp value to min/max range
   */
  const clampValue = useCallback((val: number): number => {
    return Math.max(min, Math.min(max, val));
  }, [min, max]);

  /**
   * Quantize value to step
   */
  const quantizeValue = useCallback((val: number): number => {
    if (step) {
      return Math.round(val / step) * step;
    }
    return val;
  }, [step]);

  /**
   * Normalize value to 0-1 range
   */
  const normalizeValue = useCallback((val: number): number => {
    return (val - min) / (max - min);
  }, [min, max]);

  /**
   * Calculate rotation angle from value (-135° to +135°)
   */
  const getRotationAngle = useCallback((val: number): number => {
    const normalized = normalizeValue(val);
    return -135 + (normalized * 270);
  }, [normalizeValue]);

  /**
   * Format value for display
   */
  const formatValue = useCallback((val: number): string => {
    // Determine precision based on step size
    let precision = 0;
    if (step < 0.01) {
      precision = 3;
    } else if (step < 0.1) {
      precision = 2;
    } else if (step < 1) {
      precision = 1;
    }
    
    // Format with precision
    const formatted = val.toFixed(precision);
    
    // Remove unnecessary trailing zeros
    // "2.00" -> "2", "2.10" -> "2.1", "2.50" -> "2.5"
    return formatted.replace(/(\.\d*?)0+$/, '$1').replace(/\.$/, '');
  }, [step]);

  /**
   * Handle mouse move - update value during drag
   */
  const handleMouseMove = useCallback((e: MouseEvent) => {
    // Calculate delta (inverted: up = increase, down = decrease)
    const deltaY = dragStartY.current - e.clientY;
    
    // Sensitivity: 1 pixel = 0.5% of range
    const range = max - min;
    const sensitivity = range / 200;
    const delta = deltaY * sensitivity;
    
    // Calculate new value
    let newValue = dragStartValue.current + delta;
    newValue = clampValue(newValue);
    newValue = quantizeValue(newValue);
    
    // Update value with animation frame
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
    }
    
    animationFrameRef.current = requestAnimationFrame(() => {
      onChange(newValue);
    });
  }, [max, min, clampValue, quantizeValue, onChange]);

  /**
   * Handle mouse up - end drag
   */
  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
    
    // Remove global mouse event listeners using stored refs
    if (mouseMoveHandlerRef.current) {
      document.removeEventListener('mousemove', mouseMoveHandlerRef.current);
    }
    if (mouseUpHandlerRef.current) {
      document.removeEventListener('mouseup', mouseUpHandlerRef.current);
    }
    
    // Call onChangeComplete if provided
    if (onChangeComplete) {
      onChangeComplete(value);
    }
  }, [value, onChangeComplete]);

  /**
   * Handle mouse down - start drag
   */
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (disabled) return;
    
    e.preventDefault();
    
    // Clean up any existing listeners first
    if (mouseMoveHandlerRef.current) {
      document.removeEventListener('mousemove', mouseMoveHandlerRef.current);
    }
    if (mouseUpHandlerRef.current) {
      document.removeEventListener('mouseup', mouseUpHandlerRef.current);
    }
    
    setIsDragging(true);
    dragStartY.current = e.clientY;
    dragStartValue.current = value;
    
    // Store current handlers in refs
    mouseMoveHandlerRef.current = handleMouseMove;
    mouseUpHandlerRef.current = handleMouseUp;
    
    // Add global mouse event listeners
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, [disabled, value, handleMouseMove, handleMouseUp]);

  /**
   * Handle mouse wheel - fine adjustment
   */
  const handleWheel = useCallback((e: React.WheelEvent) => {
    if (disabled) return;
    
    e.preventDefault();
    
    // Calculate delta (wheel up = increase, wheel down = decrease)
    const delta = -Math.sign(e.deltaY) * step;
    
    let newValue = value + delta;
    newValue = clampValue(newValue);
    newValue = quantizeValue(newValue);
    
    onChange(newValue);
    
    if (onChangeComplete) {
      onChangeComplete(newValue);
    }
  }, [disabled, value, step, clampValue, quantizeValue, onChange, onChangeComplete]);

  /**
   * Handle double click - reset to default
   */
  const handleDoubleClick = useCallback(() => {
    if (disabled) return;
    
    onChange(defaultValue);
    
    if (onChangeComplete) {
      onChangeComplete(defaultValue);
    }
  }, [disabled, defaultValue, onChange, onChangeComplete]);

  /**
   * Handle keyboard input
   */
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (disabled) return;
    
    let delta = 0;
    let handled = false;
    
    switch (e.key) {
      case 'ArrowUp':
      case 'ArrowRight':
        delta = step;
        handled = true;
        break;
      case 'ArrowDown':
      case 'ArrowLeft':
        delta = -step;
        handled = true;
        break;
      case 'PageUp':
        delta = step * 10;
        handled = true;
        break;
      case 'PageDown':
        delta = -step * 10;
        handled = true;
        break;
      case 'Home':
        onChange(min);
        if (onChangeComplete) onChangeComplete(min);
        handled = true;
        break;
      case 'End':
        onChange(max);
        if (onChangeComplete) onChangeComplete(max);
        handled = true;
        break;
    }
    
    if (delta !== 0) {
      let newValue = value + delta;
      newValue = clampValue(newValue);
      newValue = quantizeValue(newValue);
      
      onChange(newValue);
      if (onChangeComplete) {
        onChangeComplete(newValue);
      }
    }
    
    if (handled) {
      e.preventDefault();
    }
  }, [disabled, value, step, min, max, clampValue, quantizeValue, onChange, onChangeComplete]);

  /**
   * Cleanup on unmount
   */
  useEffect(() => {
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      // Clean up event listeners using refs
      if (mouseMoveHandlerRef.current) {
        document.removeEventListener('mousemove', mouseMoveHandlerRef.current);
      }
      if (mouseUpHandlerRef.current) {
        document.removeEventListener('mouseup', mouseUpHandlerRef.current);
      }
    };
  }, []); // Empty deps - only run on unmount

  // Calculate rotation angle
  const rotationAngle = getRotationAngle(value);
  
  // Build class names
  const containerClasses = [
    'rotary-knob',
    `rotary-knob-${size}`,
    theme && `rotary-knob-${theme}`,
    disabled && 'rotary-knob-disabled',
    isDragging && 'rotary-knob-dragging',
    isHovered && 'rotary-knob-hovered',
    isFocused && 'rotary-knob-focused',
  ].filter(Boolean).join(' ');

  const knobContent = (
    <div className={containerClasses} ref={containerRef}>
      {/* Knob SVG */}
      <div
        className="rotary-knob-container"
        onMouseDown={handleMouseDown}
        onDoubleClick={handleDoubleClick}
        onWheel={handleWheel}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        onKeyDown={handleKeyDown}
        tabIndex={disabled ? -1 : 0}
        role="slider"
        aria-label={label}
        aria-valuemin={min}
        aria-valuemax={max}
        aria-valuenow={value}
        aria-valuetext={`${formatValue(value)} ${unit}`}
        aria-disabled={disabled}
        aria-describedby={tooltip ? `${uniqueId}-tooltip` : undefined}
      >
        <svg
          className="rotary-knob-svg"
          viewBox="0 0 100 100"
          xmlns="http://www.w3.org/2000/svg"
        >
          {/* Gradient definitions */}
          <defs>
            {/* Knob base gradient - more 3D appearance */}
            <radialGradient id={`knob-gradient-${uniqueId}`} cx="40%" cy="40%">
              <stop offset="0%" stopColor="#4a4a4a" />
              <stop offset="40%" stopColor="#2a2a2a" />
              <stop offset="100%" stopColor="#0a0a0a" />
            </radialGradient>
            
            {/* Highlight gradient */}
            <radialGradient id={`highlight-gradient-${uniqueId}`} cx="35%" cy="30%">
              <stop offset="0%" stopColor="rgba(255, 255, 255, 0.4)" />
              <stop offset="50%" stopColor="rgba(255, 255, 255, 0.1)" />
              <stop offset="100%" stopColor="rgba(255, 255, 255, 0)" />
            </radialGradient>
            
            {/* Shadow gradient */}
            <radialGradient id={`shadow-gradient-${uniqueId}`} cx="50%" cy="50%">
              <stop offset="0%" stopColor="rgba(0, 0, 0, 0)" />
              <stop offset="70%" stopColor="rgba(0, 0, 0, 0.2)" />
              <stop offset="100%" stopColor="rgba(0, 0, 0, 0.5)" />
            </radialGradient>
          </defs>
          
          {/* Shadow */}
          <circle
            cx="50"
            cy="52"
            r="46"
            fill={`url(#shadow-gradient-${uniqueId})`}
            opacity="0.6"
          />
          
          {/* Outer ring - bezel */}
          <circle
            cx="50"
            cy="50"
            r="48"
            className="rotary-knob-outer-ring"
            fill="none"
            stroke="var(--color-border)"
            strokeWidth="2"
          />
          
          {/* Knob base */}
          <circle
            cx="50"
            cy="50"
            r="42"
            className="rotary-knob-base"
            fill={`url(#knob-gradient-${uniqueId})`}
          />
          
          {/* Highlight overlay */}
          <circle
            cx="50"
            cy="50"
            r="42"
            className="rotary-knob-highlight"
            fill={`url(#highlight-gradient-${uniqueId})`}
          />
          
          {/* Center dot for reference */}
          <circle
            cx="50"
            cy="50"
            r="3"
            fill="rgba(0, 0, 0, 0.5)"
          />
          
          {/* Indicator line - thicker and more prominent */}
          <line
            x1="50"
            y1="12"
            x2="50"
            y2="28"
            className="rotary-knob-indicator"
            stroke="var(--color-accent)"
            strokeWidth="4"
            strokeLinecap="round"
            transform={`rotate(${rotationAngle} 50 50)`}
          />
          
          {/* Indicator dot at the end for better visibility */}
          <circle
            cx="50"
            cy="20"
            r="3"
            fill="var(--color-accent)"
            transform={`rotate(${rotationAngle} 50 50)`}
            style={{ filter: 'drop-shadow(0 0 3px var(--color-accent))' }}
          />
        </svg>
      </div>
      
      {/* Label */}
      <div className="rotary-knob-label">{label}</div>
      
      {/* Value display */}
      <div className="rotary-knob-value">
        {formatValue(value)}{unit && <span className="rotary-knob-unit">{unit}</span>}
      </div>
    </div>
  );

  // Wrap with tooltip if provided
  if (tooltip) {
    return (
      <Tooltip content={tooltip} position="top" delay={300}>
        {knobContent}
      </Tooltip>
    );
  }

  return knobContent;
}
